import { CalendarCheck, Check, X } from 'lucide-react'
import { HelpIcon, ApiError, Button, EndpointHint } from '../../core/index.js'
import { useClinicalBridge, useRespondToBridgeCallout, useWorkerBridgeCallouts } from '../hooks.js'
import type { WorkerBridgeCallout } from '../types.js'
import { pluralMinutes, pluralPoints } from './bridgeText.js'
import { describeCalloutShift, formatInstant } from './bridgeCalloutText.js'
import { cardCallouts, describePay, describeResult } from './bridgeCardData.js'

/**
 * The Open shift card on the worker's Home (plan section 7, the page's step
 * 3): each Clinical Bridge callout the worker can still answer - the
 * shift, pay where the facility showed it, the arrival rule, what the shift
 * earns when worked - with I'll take it and Can't make it, then what became
 * of the ones that settled in the last day. Polled every half minute. The
 * card renders nothing for a worker with nothing to show, so Home is
 * unchanged for most workers most days.
 */
export function BridgeOpenShiftCard({ workerId }: { workerId: string }) {
  const callouts = useWorkerBridgeCallouts(workerId)
  const bridge = useClinicalBridge(workerId)
  const respond = useRespondToBridgeCallout(workerId)
  if (!callouts.isSuccess) {
    return null
  }
  const { open, settled } = cardCallouts(callouts.data, new Date())
  if (open.length === 0 && settled.length === 0) {
    return null
  }
  const boost = bridge.data?.rules.scoreBoostPerShift ?? 0
  const failure = respond.isError ? (respond.error instanceof ApiError ? respond.error.message : 'Your answer could not be saved. Try again.') : null

  return (
    <section aria-label="Clinical Bridge callouts">
      <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">
        {open.length > 0 ? (open.length === 1 ? 'Open shift' : `${open.length} open shifts`) : 'Clinical Bridge'}
        <HelpIcon topic="schedule.worker-bridge" className="ml-1" />
        <EndpointHint className="ml-2">GET /workers/{'{workerId}'}/bridge/callouts</EndpointHint>
      </div>
      <ul className="flex flex-col gap-2">
        {open.map((callout) => (
          <li key={callout.calloutId} className="border border-coral/50 bg-coral-lt p-3">
            <OpenCallout callout={callout} boost={boost} pending={respond.isPending} onAnswer={(answer) => respond.mutate({ calloutId: callout.calloutId, answer })} />
          </li>
        ))}
        {settled.map((callout) => (
          <li key={callout.calloutId} className={`border p-3 ${callout.result === 'yours' ? 'border-green/50 bg-green/10' : 'border-marine/15 bg-white'}`}>
            <SettledCallout callout={callout} boost={boost} />
          </li>
        ))}
      </ul>
      {failure && (
        <p role="alert" className="mt-1.5 text-[11px] text-coral">
          {failure}
        </p>
      )}
    </section>
  )
}

function OpenCallout({ callout, boost, pending, onAnswer }: { callout: WorkerBridgeCallout; boost: number; pending: boolean; onAnswer: (answer: 'accept' | 'decline') => void }) {
  const pay = describePay(callout)
  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="text-sm font-extrabold text-ink">{describeCalloutShift(callout)}</div>
        <div className="text-[11px] text-ink-mid">Needs covering · open until {formatInstant(callout.expiresAt)}</div>
      </div>
      <ul className="text-[11px] text-ink leading-relaxed">
        {pay && <li>{pay}.</li>}
        <li>If you accept, be at the facility within {pluralMinutes(callout.arrivalWindowMinutes)}.</li>
        <li>{boost > 0 ? `Worked, it earns ${pluralPoints(boost)}.` : 'It counts like any other shift you work.'}</li>
        <li>First to accept gets it. No penalty for declining.</li>
      </ul>
      {callout.myAnswer?.answer === 'decline' && <p className="text-[11px] text-ink-mid">You declined. It is still open if you change your mind.</p>}
      <div className="flex flex-wrap gap-2">
        <Button icon={Check} size="sm" disabled={pending} onClick={() => onAnswer('accept')}>
          I'll take it
        </Button>
        {callout.myAnswer?.answer !== 'decline' && (
          <Button icon={X} variant="secondary" size="sm" disabled={pending} onClick={() => onAnswer('decline')}>
            Can't make it
          </Button>
        )}
      </div>
    </div>
  )
}

function SettledCallout({ callout, boost }: { callout: WorkerBridgeCallout; boost: number }) {
  const yours = callout.result === 'yours'
  return (
    <div className="flex items-start gap-2.5">
      {yours && (
        <div aria-hidden="true" className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-green/15 text-green">
          <CalendarCheck strokeWidth={2.25} className="w-4 h-4" />
        </div>
      )}
      <div className="min-w-0">
        <div className={`text-[11px] ${yours ? 'font-extrabold text-ink' : 'font-bold text-ink-mid'}`}>{describeCalloutShift(callout)}</div>
        <div className="text-[11px] text-ink leading-relaxed">{describeResult(callout)}</div>
        {yours && boost > 0 && <div className="text-[11px] text-ink-mid leading-relaxed">{callout.outcome?.kind === 'completed' ? `Worked: ${pluralPoints(boost)} count toward your score.` : `Worked, it earns ${pluralPoints(boost)}.`}</div>}
        {yours && callout.outcome && callout.outcome.kind !== 'completed' && <div className="text-[11px] text-ink-mid leading-relaxed">{callout.outcome.kind === 'no_show' ? 'Reported as a no-show.' : 'Reported as a call-out.'}</div>}
      </div>
    </div>
  )
}
